import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  AlertTriangle,
  Fish,
  Heart,
  Loader2,
  Lock,
  RefreshCw,
  ShoppingBasket,
  Shell,
  Trash2,
  Users,
  Waves,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGroceryList } from '@/hooks/useGroceryList';
import { useToast } from '@/hooks/useToast';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { cn } from '@/lib/utils';
import { AddItemForm } from '@/components/grocery/AddItemForm';
import { GroceryItemRow } from '@/components/grocery/GroceryItemRow';
import { SharePartnerDialog } from '@/components/grocery/SharePartnerDialog';

const Index = () => {
  useSeoMeta({
    title: "Ponyo's Pantry",
    description: "A sweet little grocery list for two.",
  });

  const { user } = useCurrentUser();

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <BackgroundDecor />
      <Header />
      <main className="relative mx-auto w-full max-w-6xl px-4 pb-24 pt-2 sm:px-6 lg:px-10">
        {user ? <GroceryListView pubkey={user.pubkey} /> : <SignedOutHero />}
      </main>
      <Footer />
    </div>
  );
};

export default Index;

/* ----------------------------------------------------------------------- */

function Header() {
  return (
    <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-5 sm:px-6 lg:px-10">
      <div className="flex items-center gap-2.5">
        <div className="relative">
          <div className="flex size-10 items-center justify-center rounded-2xl bg-gradient-to-br from-coral via-coral-deep to-coral text-primary-foreground shadow-lg shadow-coral/30">
            <Fish className="size-5 -scale-x-100" strokeWidth={2.25} />
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-turquoise ring-2 ring-background" />
        </div>
        <div>
          <h1 className="text-lg font-bold leading-none tracking-tight">
            Ponyo's <span className="text-coral">Pantry</span>
          </h1>
          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 italic">
            ham · sandwiches · everything good
          </p>
        </div>
      </div>
      <LoginArea className="max-w-60" />
    </header>
  );
}

function Footer() {
  return (
    <footer className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-8 sm:px-6 lg:px-10">
      <p className="text-center text-xs text-muted-foreground/80">
        Vibed with{' '}
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-foreground/80 underline-offset-4 hover:text-coral hover:underline"
        >
          Shakespeare
        </a>
      </p>
    </footer>
  );
}

/** Decorative gradient blobs that sit behind the content. */
function BackgroundDecor() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      <div className="absolute -top-40 -right-32 size-[34rem] rounded-full bg-coral/15 blur-3xl dark:bg-coral/20" />
      <div className="absolute -bottom-40 -left-32 size-[30rem] rounded-full bg-turquoise/15 blur-3xl dark:bg-turquoise/20" />
      <div className="absolute top-1/2 left-1/3 size-[22rem] rounded-full bg-sunlit/15 blur-3xl dark:bg-sunlit/10" />
    </div>
  );
}

/* ----------------------------------------------------------------------- */

function SignedOutHero() {
  return (
    <div className="pt-6 sm:pt-10 lg:pt-16">
      {/* Two-column hero on desktop, stacked on mobile/tablet. */}
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 lg:items-center">
        {/* Left column — headline + copy. Centered on mobile, left-aligned on
            desktop so the two columns visually balance. */}
        <div className="space-y-5 text-center lg:text-left">
          <div
            className="flex items-center justify-center lg:justify-start gap-3 text-coral/70"
            aria-hidden
          >
            <Shell className="size-5 lg:size-6 animate-drift" style={{ animationDelay: '0s' }} />
            <Fish
              className="size-7 lg:size-9 -scale-x-100 text-coral animate-drift"
              style={{ animationDelay: '0.6s' }}
            />
            <Waves
              className="size-5 lg:size-6 text-turquoise animate-drift"
              style={{ animationDelay: '1.2s' }}
            />
          </div>

          <h2 className="font-bold tracking-tight text-balance text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05]">
            A little kitchen{' '}
            <span className="bg-gradient-to-r from-coral via-coral-deep to-turquoise bg-clip-text text-transparent">
              by the sea
            </span>
            .
          </h2>

          <p className="mx-auto lg:mx-0 max-w-md lg:max-w-lg text-muted-foreground text-pretty text-base sm:text-lg lg:text-xl">
            Where you and your favorite person keep a little list of all the
            things to bring home — ham for the goldfish, ramen for the storm,
            and sometimes flowers, just because.
          </p>

          <p className="inline-flex items-center gap-1.5 rounded-full bg-turquoise/10 px-3 py-1 text-xs text-teal dark:text-turquoise">
            <Lock className="size-3" />
            Sealed in a little glass bottle — only the two of you can read inside.
          </p>
        </div>

        {/* Right column — the sign-in card. */}
        <Card className="border-border/60 bg-card/80 backdrop-blur-sm shadow-sm lg:shadow-lg">
          <CardHeader className="text-center pb-3 lg:pb-4">
            <div className="mx-auto flex size-12 lg:size-14 items-center justify-center rounded-2xl bg-coral/15 text-coral mb-2">
              <Heart className="size-6 lg:size-7 fill-current" />
            </div>
            <CardTitle className="text-xl lg:text-2xl">Pop in to begin</CardTitle>
            <CardDescription className="text-pretty lg:text-base">
              Both of you sign in with a little browser key — try{' '}
              <a
                href="https://getalby.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-foreground hover:text-coral underline-offset-4 hover:underline"
              >
                Alby
              </a>{' '}
              or{' '}
              <a
                href="https://github.com/fiatjaf/nos2x"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-foreground hover:text-coral underline-offset-4 hover:underline"
              >
                nos2x
              </a>
              . Tap{' '}
              <span className="font-semibold text-foreground">Join</span> in the
              corner, then{' '}
              <span className="font-semibold text-foreground">
                Log in with extension
              </span>
              .
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 pt-2">
              <FeatureCell
                num="1"
                title="Sign in"
                desc="Each of you brings your own little key."
              />
              <FeatureCell
                num="2"
                title="Hold hands"
                desc="Trade codes once and you're paired forever."
              />
              <FeatureCell
                num="3"
                title="Wander"
                desc="Add things, check things, the list follows you."
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FeatureCell({
  num,
  title,
  desc,
}: {
  num: string;
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-xl border border-border/40 bg-seafoam/30 dark:bg-muted/30 p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex size-5 items-center justify-center rounded-full bg-coral/20 text-[10px] font-bold text-coral-deep">
          {num}
        </span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground text-pretty">{desc}</p>
    </div>
  );
}

/* ----------------------------------------------------------------------- */

interface GroceryListViewProps {
  pubkey: string;
}

function GroceryListView({ pubkey }: GroceryListViewProps) {
  const list = useGroceryList();
  const { toast } = useToast();
  const [shareOpen, setShareOpen] = useState(false);

  const partner = list.participants[0];
  const partnerAuthor = useAuthor(partner);
  const partnerMeta = partnerAuthor.data?.metadata;
  const partnerName = partner
    ? partnerMeta?.name ?? genUserName(partner)
    : '';

  const checkedCount = useMemo(
    () => list.items.filter((i) => i.checked).length,
    [list.items],
  );
  const remaining = list.items.length - checkedCount;
  const allDone = list.items.length > 0 && remaining === 0;

  const handleToggle = async (id: string) => {
    try {
      await list.toggleItem(id);
    } catch (e) {
      toast({
        title: "Couldn't update",
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRemove = async (id: string) => {
    const item = list.items.find((i) => i.id === id);
    if (!item) return;
    if (item.addedBy !== pubkey) {
      toast({
        title: 'Only the person who added an item can remove it',
        description: `Ask ${partnerName || 'your partner'} to remove "${item.name}".`,
      });
      return;
    }
    try {
      await list.removeItem(id);
    } catch (e) {
      toast({
        title: "Couldn't remove",
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAdd = async (input: { name: string; qty?: string }) => {
    try {
      await list.addItem(input);
    } catch (e) {
      toast({
        title: "Couldn't add",
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
      throw e;
    }
  };

  const handleClearChecked = async () => {
    try {
      await list.clearChecked();
    } catch (e) {
      toast({
        title: "Couldn't clear",
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="pt-2">
      {/* Banners (full-width above the two columns) */}
      {!list.signerSupportsEncryption && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <AlertTriangle className="size-4 mt-0.5 shrink-0 text-destructive" />
          <div className="space-y-1">
            <p className="font-medium text-destructive">
              Your sign-in extension can't lock the pantry door.
            </p>
            <p className="text-xs text-muted-foreground">
              Please update your Nostr extension (Alby or nos2x) to a
              version that supports NIP-44 encryption, then refresh.
              Items can't be saved until then.
            </p>
          </div>
        </div>
      )}

      {list.signerSupportsEncryption && list.hasDecryptionError && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-sunlit/40 bg-sunlit/10 p-3 text-sm">
          <AlertTriangle className="size-4 mt-0.5 shrink-0 text-sunlit" />
          <div className="space-y-1">
            <p className="font-medium">Couldn't read part of the list.</p>
            <p className="text-xs text-muted-foreground">
              An older entry was sealed with a different key. New items
              will be saved correctly.
            </p>
          </div>
        </div>
      )}

      {/* Two-column layout on lg+. Below lg, the panel renders as a thin
          status row inline above the list (preserves mobile design). */}
      <div className="grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-10 lg:items-start">
        {/* ─── Left: Kitchen Window panel ─────────────────────────────── */}
        <KitchenWindow
          partner={partner}
          partnerName={partnerName}
          partnerPicture={partnerMeta?.picture}
          partnerOnline={!!list.events.some((e) => e.pubkey === partner)}
          remaining={remaining}
          checkedCount={checkedCount}
          totalCount={list.items.length}
          allDone={allDone}
          isLoading={list.isLoading}
          isFetching={list.isFetching}
          signerSupportsEncryption={list.signerSupportsEncryption}
          onShare={() => setShareOpen(true)}
          onRefresh={() => list.refetch()}
        />

        {/* ─── Right: List + add form ─────────────────────────────────── */}
        <div className="space-y-5 lg:max-w-2xl">
          {/* Add form */}
          <AddItemForm
            onAdd={handleAdd}
            disabled={list.isMutating || !list.signerSupportsEncryption}
          />

          {/* Counts row — visible on all sizes; mirrors what's in the
              KitchenWindow on desktop, but stays here so the "Clear basket"
              action sits next to the list it acts on. */}
          {list.items.length > 0 && (
            <div className="flex items-center justify-between px-1">
              <p className="text-xs text-muted-foreground">
                {remaining > 0 ? (
                  <>
                    <span className="font-semibold text-foreground">
                      {remaining}
                    </span>{' '}
                    to fetch
                    {checkedCount > 0 && (
                      <>
                        {' · '}
                        <span className="text-teal dark:text-turquoise">
                          {checkedCount} in the basket
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="text-teal dark:text-turquoise font-semibold">
                    Everything gathered ✿
                  </span>
                )}
              </p>
              {checkedCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearChecked}
                  disabled={list.isMutating}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3 mr-1" />
                  Clear basket
                </Button>
              )}
            </div>
          )}

          {/* List */}
          <Card
            className={cn(
              'border-border/50 bg-card/70 backdrop-blur-sm shadow-sm',
              allDone && 'border-turquoise/40',
            )}
          >
            <CardContent className="p-2 sm:p-3">
              {list.isLoading ? (
                <ul className="space-y-1">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                    >
                      <Skeleton className="size-5 rounded-md" />
                      <Skeleton className="h-4 flex-1 max-w-[60%]" />
                      <Skeleton className="size-6 rounded-full" />
                    </li>
                  ))}
                </ul>
              ) : list.items.length === 0 ? (
                <EmptyState
                  hasPartner={!!partner}
                  onShare={() => setShareOpen(true)}
                />
              ) : (
                <ul className="space-y-0.5">
                  {list.items.map((item) => (
                    <GroceryItemRow
                      key={item.id}
                      item={item}
                      myPubkey={pubkey}
                      onToggle={handleToggle}
                      onRemove={handleRemove}
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Hint when paired but partner hasn't published yet */}
          {partner && !list.events.some((e) => e.pubkey === partner) && (
            <p className="px-2 text-center text-xs text-muted-foreground italic">
              Waiting for {partnerName} to wave back. Their things will drift
              in once they do.
            </p>
          )}
        </div>
      </div>

      <SharePartnerDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        myPubkey={pubkey}
        currentPartner={partner}
        onSetPartner={list.setParticipant}
      />
    </div>
  );
}

/* ----------------------------------------------------------------------- */

interface KitchenWindowProps {
  partner: string | undefined;
  partnerName: string;
  partnerPicture?: string;
  partnerOnline: boolean;
  remaining: number;
  checkedCount: number;
  totalCount: number;
  allDone: boolean;
  isLoading: boolean;
  isFetching: boolean;
  signerSupportsEncryption: boolean;
  onShare: () => void;
  onRefresh: () => void;
}

/**
 * The "kitchen window" panel that fills the empty space on desktop.
 *
 * Two distinct presentations:
 *
 * - On mobile / tablet (< lg): renders as a single horizontal status row —
 *   sharing pill, sealed pill, fetching indicator, refresh. Same pixels as
 *   the original mobile design.
 *
 * - On desktop (lg+): expands into a tall, illustrated panel with a big
 *   "remaining" number, a stylized drift scene, the partner's avatar, and
 *   the same controls promoted into prominent rows. Gives the eye somewhere
 *   to land instead of leaving 700px of empty space next to the list.
 */
function KitchenWindow({
  partner,
  partnerName,
  partnerPicture,
  partnerOnline,
  remaining,
  checkedCount,
  totalCount,
  allDone,
  isLoading,
  isFetching,
  signerSupportsEncryption,
  onShare,
  onRefresh,
}: KitchenWindowProps) {
  return (
    <>
      {/* ─── Mobile/Tablet status row (hidden on lg+) ─── */}
      <div className="flex flex-wrap items-center justify-between gap-3 lg:hidden">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onShare}
            className={cn(
              'group flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-all',
              partner
                ? 'border-coral/40 bg-coral/10 text-coral-deep hover:bg-coral/15'
                : 'border-dashed border-border bg-card/50 text-muted-foreground hover:border-coral/40 hover:text-foreground',
            )}
          >
            {partner ? (
              <>
                <Heart className="size-3.5 fill-current" />
                <span className="font-medium">Sharing with {partnerName}</span>
              </>
            ) : (
              <>
                <Users className="size-3.5" />
                <span>Share with your partner</span>
              </>
            )}
          </button>

          {signerSupportsEncryption && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-turquoise/10 px-2 py-1 text-[10px] font-medium text-teal dark:text-turquoise"
              title="Items are sealed with end-to-end encryption. Only you and your partner can read them."
            >
              <Lock className="size-2.5" />
              sealed
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isFetching && !isLoading && (
            <Loader2 className="size-3.5 text-muted-foreground animate-spin" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onRefresh}
            className="size-8 text-muted-foreground hover:text-foreground"
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* ─── Desktop panel (hidden below lg) ─── */}
      <aside className="hidden lg:block lg:sticky lg:top-6">
        <Card
          className={cn(
            'overflow-hidden border-border/50 bg-card/70 backdrop-blur-sm shadow-sm',
            allDone && 'border-turquoise/40',
          )}
        >
          {/* Illustrated header — drifting little scene */}
          <div className="relative h-32 overflow-hidden bg-gradient-to-br from-coral/20 via-sunlit/15 to-turquoise/25">
            <div
              className="absolute inset-0 opacity-60"
              aria-hidden
              style={{
                backgroundImage:
                  'radial-gradient(circle at 30% 80%, hsl(176 70% 45% / 0.25), transparent 50%), radial-gradient(circle at 80% 20%, hsl(8 85% 65% / 0.25), transparent 50%)',
              }}
            />
            <div
              className="absolute inset-0 flex items-end justify-around pb-3 px-4 text-coral/80"
              aria-hidden
            >
              <Shell
                className="size-6 animate-drift"
                style={{ animationDelay: '0s' }}
              />
              <Fish
                className="size-9 -scale-x-100 text-coral animate-drift"
                style={{ animationDelay: '0.6s' }}
              />
              <Waves
                className="size-6 text-turquoise animate-drift"
                style={{ animationDelay: '1.2s' }}
              />
              <Shell
                className="size-5 animate-drift"
                style={{ animationDelay: '1.8s' }}
              />
            </div>
          </div>

          <CardContent className="space-y-5 p-5 pt-4">
            {/* Big remaining count */}
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Today's pantry
              </p>
              <div className="flex items-baseline gap-2 mt-1">
                {isLoading ? (
                  <Skeleton className="h-12 w-20" />
                ) : allDone ? (
                  <span className="text-2xl font-semibold text-teal dark:text-turquoise">
                    Everything gathered ✿
                  </span>
                ) : totalCount === 0 ? (
                  <span className="text-2xl font-semibold text-muted-foreground">
                    Bare shelves
                  </span>
                ) : (
                  <>
                    <span className="text-5xl font-bold tabular-nums text-coral">
                      {remaining}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {remaining === 1 ? 'thing' : 'things'} to fetch
                    </span>
                  </>
                )}
              </div>
              {checkedCount > 0 && !allDone && (
                <p className="mt-1 text-xs text-teal dark:text-turquoise">
                  {checkedCount} already in the basket
                </p>
              )}
            </div>

            {/* Partner card / share prompt */}
            {partner ? (
              <button
                onClick={onShare}
                className="group w-full flex items-center gap-3 rounded-xl border border-coral/30 bg-coral/5 p-3 text-left transition-all hover:border-coral/50 hover:bg-coral/10"
              >
                <Avatar className="size-10 shrink-0 ring-2 ring-coral/30">
                  <AvatarImage
                    src={sanitizeUrl(partnerPicture)}
                    alt={partnerName}
                  />
                  <AvatarFallback className="bg-coral/20 text-coral-deep text-xs font-semibold">
                    {partnerName.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <Heart className="size-3 text-coral fill-current shrink-0" />
                    <p className="text-sm font-medium truncate">
                      {partnerName}
                    </p>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {partnerOnline ? "you're paired" : 'waiting to be added back'}
                  </p>
                </div>
              </button>
            ) : (
              <button
                onClick={onShare}
                className="group w-full flex items-center gap-3 rounded-xl border border-dashed border-border bg-card/40 p-3 text-left transition-all hover:border-coral/40 hover:bg-coral/5"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground group-hover:bg-coral/15 group-hover:text-coral transition-colors">
                  <Users className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    Invite your sweetheart
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Trade codes once and you're paired
                  </p>
                </div>
              </button>
            )}

            {/* Status footer — sealed indicator + refresh */}
            <div className="flex items-center justify-between pt-1">
              {signerSupportsEncryption ? (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium text-teal dark:text-turquoise"
                  title="Items are sealed with end-to-end encryption. Only you and your partner can read them."
                >
                  <Lock className="size-3" />
                  Sealed in a glass bottle
                </span>
              ) : (
                <span className="text-[11px] text-muted-foreground">
                  Encryption unavailable
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onRefresh}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                title="Refresh"
              >
                <RefreshCw
                  className={cn('size-3 mr-1', isFetching && 'animate-spin')}
                />
                {isFetching ? 'Checking…' : 'Refresh'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </aside>
    </>
  );
}

/* ----------------------------------------------------------------------- */

function EmptyState({
  hasPartner,
  onShare,
}: {
  hasPartner: boolean;
  onShare: () => void;
}) {
  return (
    <div className="px-6 py-10 text-center space-y-3">
      <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-seafoam dark:bg-muted/60 text-teal dark:text-turquoise">
        <ShoppingBasket className="size-5" />
      </div>
      <p className="text-sm text-muted-foreground max-w-xs mx-auto text-pretty">
        The pantry's bare. Add the first little thing above
        {hasPartner ? (
          <> — your sweetheart will see it next time they peek in.</>
        ) : (
          <>
            , or{' '}
            <button
              onClick={onShare}
              className="font-medium text-coral hover:underline underline-offset-4"
            >
              invite your sweetheart
            </button>{' '}
            first.
          </>
        )}
      </p>
    </div>
  );
}
